import type { SortBy, SortOrder } from "@/common/hooks/useSort";
import { Button } from "@/components/ui/button/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { MonthPicker } from "@/components/ui/month-picker";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Typography from "@/components/ui/typography";
import { cn, isMobileDevice } from "@/lib/utils";
import { Share } from "lucide-react";

type TransactionType = "deposit" | "withdrawal";
const transactionTypes: TransactionType[] = ["deposit", "withdrawal"];
type Props = {
	isOpen: boolean;
	onClose: () => void;
	transactionType: TransactionType | undefined;
	setTransactionType: (type: TransactionType) => void;
	sortBy: SortBy;
	handleSortFieldChange: (field: SortBy) => void;
	sortOrder: SortOrder;
	handleSortOrderChange: (order: SortOrder) => void;
	onExport: () => void;
	onApply: () => void;
	onReset: () => void;
	selectedMonth?: Date;
	onMonthSelect?: (date: Date) => void;
};

const KASHU_FOUNDED_YEAR = 2025;

const ActivityModal = ({
	isOpen,
	onClose,
	transactionType,
	setTransactionType,
	sortBy,
	handleSortFieldChange,
	sortOrder,
	handleSortOrderChange,
	onExport,
	onApply,
	onReset,
	selectedMonth,
	onMonthSelect,
}: Props) => {
	const sortOptions: { label: string; field: SortBy; order: SortOrder }[] = [
		{ label: "Date (Newest First)", field: "date", order: "desc" },
		{ label: "Date (Oldest First)", field: "date", order: "asc" },
		{ label: "Amount (High to Low)", field: "type", order: "desc" },
		{ label: "Amount (Low to High)", field: "type", order: "asc" },
	];

	const handleApply = () => {
		onApply();
		onClose();
	};

	// Shared content component
	const ModalContent = () => (
		<>
			{/* Filter by transaction type (radio buttons) */}
			<div className="flex flex-col gap-2 mt-4">
				<Typography variant="h5">Transaction Type</Typography>
				<RadioGroup
					value={transactionType}
					onValueChange={setTransactionType}
					className="flex flex-col gap-2 mt-2"
				>
					{transactionTypes.map((type) => (
						<div className="flex items-center space-x-2" key={type}>
							<RadioGroupItem value={type} id={`type-${type}`} />
							<Label
								htmlFor={`type-${type}`}
								className={
									transactionType === type
										? "text-textDark font-medium"
										: "text-muted-foreground"
								}
							>
								{type.charAt(0).toUpperCase() + type.slice(1)}
							</Label>
						</div>
					))}
				</RadioGroup>
			</div>

			{/* Sort by */}
			<div className="mt-6">
				<Typography variant="h5">Sort By</Typography>
				<RadioGroup
					value={`${sortBy}-${sortOrder}`}
					onValueChange={(val) => {
						const [field, order] = val.split("-");
						handleSortFieldChange(field as SortBy);
						handleSortOrderChange(order as SortOrder);
					}}
					className="flex flex-col gap-2 mt-2"
				>
					{sortOptions.map(({ label, field, order }) => {
						const value = `${field}-${order}`;
						return (
							<div className="flex items-center space-x-2" key={label}>
								<RadioGroupItem value={value} id={`sort-${value}`} />
								<Label
									htmlFor={`sort-${value}`}
									className={
										`${sortBy}-${sortOrder}` === value
											? "text-textDark font-medium"
											: "text-muted-foreground"
									}
								>
									{label}
								</Label>
							</div>
						);
					})}
				</RadioGroup>
			</div>

			<div className="flex flex-col gap-2 mt-4">
				<Typography variant="h5">Filter by Month</Typography>

				<Popover>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							className={cn(
								"w-full justify-start text-left font-normal",
								!selectedMonth && "text-muted-foreground",
							)}
						>
							{selectedMonth ? (
								selectedMonth.toLocaleDateString("en-US", {
									month: "long",
									year: "numeric",
								})
							) : (
								<span>Select month</span>
							)}
						</Button>
					</PopoverTrigger>
					<PopoverContent
						side="top"
						align="start"
						className=" min-w-full p-4 max-w-sm w-[var(--radix-popover-trigger-width)]"
					>
						<div className="flex justify-center  w-full">
							<MonthPicker
								selectedMonth={selectedMonth}
								onMonthSelect={onMonthSelect}
								minDate={new Date(KASHU_FOUNDED_YEAR, 0, 1)}
							/>
						</div>
					</PopoverContent>
				</Popover>
			</div>

			{/* Export Transactions */}
			<div className="mt-6 border-t pt-4">
				<Button
					variant="outline"
					className="w-full flex items-center gap-2 justify-center"
					onClick={onExport}
				>
					<Share />
					Export Transactions
				</Button>
			</div>
		</>
	);

	// Footer component
	const FooterContent = () => (
		<div className="mt-6 gap-4 flex">
			<Button variant="outline" className="flex-1" onClick={onReset}>
				Reset
			</Button>
			<Button className="flex-1" onClick={handleApply}>
				Apply
			</Button>
		</div>
	);

	// Render based on device type
	return isMobileDevice() ? (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent className="p-4">
				<DrawerHeader>
					<DrawerTitle>
						<Typography variant="h4">Filter & Sort</Typography>
					</DrawerTitle>
				</DrawerHeader>
				<div className="">
					<ModalContent />
				</div>
				<DrawerFooter>
					<FooterContent />
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	) : (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-xl w-full sm:rounded-2xl rounded-none sm:my-4 my-0 p-4 max-h-screen overflow-y-auto">
				<DialogHeader>
					<DialogTitle>
						<Typography variant="h4">Filter & Sort</Typography>
					</DialogTitle>
				</DialogHeader>
				<ModalContent />
				<DialogFooter className="mt-6 gap-4">
					<Button variant="outline" className="flex-1" onClick={onReset}>
						Reset
					</Button>
					<Button className="flex-1" onClick={handleApply}>
						Apply
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ActivityModal;
